import { COMPANY_STATUS_LABELS } from '@l2u/shared/companyStatusLabels';
import type { CompanyStatus } from './types.ts';

/**
 * Human-readable labels for each company status. Single source of truth shared
 * by the status badge, the admin filters and the admin digest email so the
 * three can never drift apart.
 *
 * The constant itself now lives in @l2u/shared/companyStatusLabels, because
 * apps/api (a Cloudflare Worker, which cannot import this React package) needs
 * the same wording for the weekly station digest. Re-exported here so every
 * existing '@l2u/ui/lib/companyStatus.ts' import is unaffected.
 */
export { COMPANY_STATUS_LABELS };

/**
 * Vendor-facing overrides for statuses whose meaning flips by audience. The
 * status is stored as a single value, but "whose turn is it" reads opposite to
 * the two sides:
 *   - `review`  = vendor submitted, the ball is with the admin. The admin sees
 *     "Action required"; to the vendor it is simply "Under Review by Admin".
 *   - `changes` = admin returned it, the ball is back with the vendor. The admin
 *     sees "Vendor change requested"; to the vendor that IS their action item.
 * Statuses not listed here fall back to COMPANY_STATUS_LABELS, so the shared
 * wording stays a single source of truth.
 */
const VENDOR_STATUS_LABEL_OVERRIDES: Partial<Record<CompanyStatus, string>> = {
  review: 'Under Review by Admin',
  changes: 'Action required',
};

/**
 * A status as the VENDOR's portal displays it. Superset of the stored
 * CompanyStatus with a derived 'action_required' — see vendorDisplayStatus.
 */
export type VendorDisplayStatus = CompanyStatus | 'action_required';

/**
 * Task 9 (display-only): the vendor reads "Action Required" whenever they owe
 * something, regardless of where the questionnaire sits in its lifecycle — so a
 * vendor with outstanding items isn't told "Under Review by Admin" while the
 * ball is actually with them.
 *
 * `companies.status` is NEVER mutated by this: the admin continues to see the
 * true lifecycle state. That means the two portals legitimately describe the
 * same vendor differently, which is the intent.
 *
 * `deactivated` is never overridden — a deactivated vendor owes nothing and
 * cannot act.
 */
export function vendorDisplayStatus(
  status: CompanyStatus,
  actionItemCount: number,
): VendorDisplayStatus {
  return actionItemCount > 0 && status !== 'deactivated' ? 'action_required' : status;
}

/**
 * Human-readable status label as the given audience should read it. `audience`
 * defaults to 'admin' (the canonical labels); 'vendor' covers the party being
 * reviewed in their own portal.
 */
export function companyStatusLabel(
  status: VendorDisplayStatus,
  audience: 'admin' | 'vendor' = 'admin',
): string {
  // Derived, audience-independent: only the vendor portal produces it, but the
  // label must never resolve to undefined if another caller passes it through.
  if (status === 'action_required') return 'Action Required';
  if (audience === 'vendor') {
    return VENDOR_STATUS_LABEL_OVERRIDES[status] ?? COMPANY_STATUS_LABELS[status];
  }
  return COMPANY_STATUS_LABELS[status];
}

/** Statuses in lifecycle order — drives the order of filter dropdown options. */
export const COMPANY_STATUSES: CompanyStatus[] = [
  'invited',
  'in_progress',
  'review',
  'changes',
  'compliant',
  'expired',
  'deactivated',
];
